import { motion } from 'motion/react';
import { Check, Sparkles } from 'lucide-react';
import { useState } from 'react';
import demoButtonLogo from '@/assets/xrepl-demo.png';

export function Demo() {
  const [activeTab, setActiveTab] = useState<'gmail' | 'linkedin'>('gmail');
  const [showVariants, setShowVariants] = useState(false);
  const [selectedTone, setSelectedTone] = useState('generous and value-focused');
  const [selectedStrategy, setSelectedStrategy] = useState('Answer Questions');
  const [insertedDraft, setInsertedDraft] = useState('');

  const toneOptions = [
    'generous and value-focused',
    'professional and action-oriented',
    'encouraging and low-pressure'
  ];

  const strategies = [
    { label: 'Answer Questions', recommended: true },
    { label: 'Schedule Call', recommended: false },
    { label: 'Share Examples', recommended: false },
    { label: 'Ask Challenges', recommended: false },
    { label: 'Offer Trial', recommended: false }
  ];

  const variants = [
    { 
      label: 'TRIAL ONBOARDING GUIDE • GENEROUS AND VALUE-FOCUSED',
      content: `Dear Cormac McShoul,

Thanks for reaching out—I'm thrilled you're interested in xRepl.ai for TestFinn's outbound team. Our AI adapts seamlessly to your personal writing style by analyzing past emails and refining drafts to match your tone, ensuring nothing sounds generic.

We've helped similar SaaS teams cut drafting time by 70%, with one boosting reply rates by 25%. Pricing starts at $49/user/month, with flexible tiers for teams.

To get you started, here's a quick onboarding guide for our 14-day free trial: Sign up at our site, upload a few emails for style training, and integrate with your CRM. Let's hop on a demo call this week to walk through it—when works for you?

Best regards,`
    },
    { 
      label: 'VALUE PROPOSITION ADD-ON • GENEROUS AND VALUE-FOCUSED',
      content: `Dear Cormac McShoul,

Great to hear from you—xRepl.ai is perfect for streamlining those 200+ daily emails at TestFinn. It excels at preserving your personal writing style by learning from your samples and generating tailored drafts that feel authentic.

SaaS teams like yours have seen huge wins: one reported 60% faster replies and more personalized outreach. Pricing is straightforward—$49/user/month base, scaling with features.

Beyond core drafting, our value add-ons include real-time analytics for email performance and A/B testing tools to optimize conversions. Ready for a demo call to explore how this elevates your team's game? Suggest a time!

Best regards,`
    },
    { 
      label: 'EASY SIGN-UP PROCESS • GENEROUS AND VALUE-FOCUSED',
      content: `Dear Cormac McShoul,

Thanks for the interest! xRepl.ai maintains your writing style by analyzing your email history and adapting each draft to match your unique voice—no generic templates here.

Results from other SaaS teams? We've seen 50-70% time savings and measurably higher reply rates within 2-3 weeks of consistent use.

Getting started is simple: 14-day free trial, quick Chrome extension install, connect Gmail, and you're drafting smarter emails in minutes. How about a 15-minute call this week to show you the platform live?

Best regards,`
    }
  ];

  const handleVariantClick = (variantContent: string) => {
    setInsertedDraft(variantContent);
    setShowVariants(false);
  };

  return (
    <section className="py-20 bg-gray-50/80 dark:bg-[#0F2847]/60 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            See It In Action
          </h2>
          <div className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto space-y-3">
            <p className="font-semibold">
              One click instantly generates multiple personalized draft variants — each perfectly matched to your email's context.
            </p>
            <p>
              xRepl.ai intelligently understands the conversation, detects the ideal professional tone, and proposes targeted response strategies (such as warm follow-up, value insight, objection handling, or strong close). Choose the draft that feels right, insert it with a single click, and send with confidence.
            </p>
          </div>
        </motion.div>

        <motion.div
          className="max-w-5xl mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Tab Switcher */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white dark:bg-[#1E3A8A]/20 rounded-lg p-1 shadow-md border border-gray-200 dark:border-[#1E3A8A]">
              <button
                onClick={() => {
                  setActiveTab('gmail');
                  setShowVariants(false);
                  setInsertedDraft('');
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'gmail'
                    ? 'bg-[#1E3A8A] text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Gmail Demo
              </button>
              <button
                onClick={() => {
                  setActiveTab('linkedin');
                  setShowVariants(false);
                  setInsertedDraft('');
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'linkedin'
                    ? 'bg-[#0A66C2] text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                LinkedIn Demo
              </button>
            </div>
          </div>

          {/* Gmail Demo */}
          {activeTab === 'gmail' && (
            <div className="bg-white dark:bg-[#1E3A8A]/20 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-[#1E3A8A]">
              {/* Window Header */}
              <div className="bg-[#f5f5f5] dark:bg-[#1E3A8A]/30 px-6 py-3 border-b border-gray-300 dark:border-[#1E3A8A] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">
                    New Message
                  </span>
                </div>
                <div className="flex gap-2">
                  <button className="w-3 h-3 rounded-full bg-gray-400 hover:bg-gray-500" />
                  <button className="w-3 h-3 rounded-full bg-gray-400 hover:bg-gray-500" />
                </div>
              </div>

              {/* Email Fields */}
              <div className="p-6 space-y-3 border-b border-gray-200 dark:border-[#1E3A8A]/30 bg-white dark:bg-[#0A1628]">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 dark:text-gray-400 w-20">To</span>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="bg-[#1E3A8A]/10 dark:bg-[#60A5FA]/20 px-3 py-1 rounded-full">
                      <span className="text-gray-700 dark:text-gray-300 text-sm">
                        Cormac McShoul (cde3690000@gmail.com)
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 border-b border-gray-100 dark:border-[#1E3A8A]/20 pb-3">
                  <span className="text-gray-500 dark:text-gray-400 w-20">Subject</span>
                  <span className="text-gray-900 dark:text-white">Re: Sales Hacker meet</span>
                </div>
              </div>

              {/* Compose Area */}
              <div className="p-6 min-h-[600px] bg-white dark:bg-[#0A1628] relative">
                {!showVariants && !insertedDraft && (
                  <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-6 space-y-4">
                    {/* Show the original email thread */}
                    <div className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-2 bg-gray-50 dark:bg-[#1E3A8A]/10 rounded">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-xs font-semibold">
                          C
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">Cormac McShoul</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">to me</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                        <p>Hi,</p>
                        <p>I saw ResponseAble mentioned in a recent Sales Hacker thread and it caught my attention. Our outbound team at TestFinn is sending 200+ personalized emails per day, but drafting replies still takes too much time and they sometimes sound generic.</p>
                        <p className="font-medium">Quick questions:</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>How well does it maintain my personal writing style?</li>
                          <li>Do you have examples of results from other SaaS teams?</li>
                          <li>How long does it take to see results?</li>
                        </ul>
                        <p>Would be great to see how it works in action if you have a few minutes for a quick call.</p>
                        <p>Thanks,<br />Cormac McShoul<br />Head of Sales Development<br />TechFlow.io</p>
                      </div>
                    </div>
                    
                    <div className="pt-2">
                      <p className="text-gray-400 dark:text-gray-500 italic">
                        Click "Respond" to generate AI-powered draft replies...
                      </p>
                    </div>
                  </div>
                )}

                {/* Inserted Draft - Shows after selecting a variant */}
                {!showVariants && insertedDraft && (
                  <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed space-y-6">
                    {/* Original email thread - collapsed */}
                    <div className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-2 bg-gray-50 dark:bg-[#1E3A8A]/10 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-xs font-semibold">
                          C
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 dark:text-white">Cormac McShoul</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>Hi,</p>
                        <p>I saw ResponseAble mentioned in a recent Sales Hacker thread and it caught my attention. Our outbound team at TestFinn is sending 200+ personalized email drafting...</p>
                      </div>
                    </div>

                    {/* Inserted AI-generated reply */}
                    <div className="whitespace-pre-line text-gray-900 dark:text-white leading-relaxed">
                      {insertedDraft}
                    </div>
                  </div>
                )}

                {/* Draft Variants Overlay - Shows when Respond is clicked */}
                {showVariants && (
                  <motion.div
                    className="absolute inset-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-600 shadow-2xl overflow-hidden flex flex-col"
                    style={{ height: '550px' }}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Header */}
                    <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      <img 
                        src={demoButtonLogo}
                        alt="xRepl.ai" 
                        className="w-6 h-6 object-contain"
                      />
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                        Able to Respond Better
                      </h3>
                      <button
                        onClick={() => setShowVariants(false)}
                        className="ml-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl leading-none"
                      >
                        ✕
                      </button>
                    </div>

                    {/* Intent Section */}
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        <span className="font-semibold">Intent:</span> inquiring about AI email drafting tool features, examples, pricing, and requesting a demo call | Topics: AI email drafting, personal writing style, SaaS team examples, pricing structure, demo call
                      </p>
                      
                      {/* Strategy Buttons */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {strategies.map((strategy) => (
                          <button
                            key={strategy.label}
                            onClick={() => setSelectedStrategy(strategy.label)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                              selectedStrategy === strategy.label
                                ? 'bg-blue-500 text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            {strategy.label}
                            {strategy.recommended && (
                              <span className="ml-1 text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded">
                                Recommended
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Goal:</span> offer a free trial or next steps
                      </p>
                    </div>

                    {/* Tone Selector */}
                    <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <label className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Tone:</span>
                        <select
                          value={selectedTone}
                          onChange={(e) => setSelectedTone(e.target.value)}
                          className="flex-1 max-w-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {toneOptions.map((tone) => (
                            <option key={tone} value={tone}>{tone}</option>
                          ))}
                        </select>
                      </label>
                    </div>

                    {/* Draft Variants - Scrollable */}
                    <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50 dark:bg-gray-900" style={{ maxHeight: '400px' }}>
                      <div className="space-y-4">
                        {variants.map((variant, index) => (
                          <motion.div
                            key={variant.label}
                            onClick={() => handleVariantClick(variant.content)}
                            className="group p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 cursor-pointer transition-all shadow-sm hover:shadow-md"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                {variant.label}
                              </span>
                              <Check className="text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" size={18} />
                            </div>
                            <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line leading-relaxed">
                              {variant.content}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Gmail Unified Toolbar - Send, Respond, and Formatting Tools in ONE ROW */}
              <div className="px-6 py-4 bg-white dark:bg-[#0A1628] border-t border-gray-200 dark:border-[#1E3A8A]/30 flex items-center justify-between">
                <div className="flex items-center gap-0">
                  {/* Send Button - Gmail Style (rounded left, straight right) */}
                  <button className="px-6 py-2.5 bg-[#0b57d0] text-white font-medium hover:bg-[#1a73e8] transition-all shadow-sm" style={{ borderBottomLeftRadius: '21px', borderBottomRightRadius: '0', borderTopLeftRadius: '21px', borderTopRightRadius: '0' }}>
                    Send
                  </button>
                  
                  {/* Respond Button - Middle (straight edges) */}
                  <button
                    onClick={() => setShowVariants(!showVariants)}
                    className="px-3 py-2.5 bg-[#D3E3FD] text-[#444746] hover:bg-[#C3D3ED] transition-all shadow-sm font-medium flex items-center gap-1.5 group h-[42px]"
                    style={{ borderRadius: '0px' }}
                  >
                    <div className="w-7 h-7 flex items-center justify-center">
                      <img 
                        src={demoButtonLogo}
                        alt="xReplAI" 
                        className="w-7 h-7 object-contain group-hover:scale-110 transition-transform"
                      />
                    </div>
                    xReplAI
                  </button>

                  {/* Dropdown Button - Gmail Style (straight left, rounded right) */}
                  <button className="px-2.5 py-2.5 bg-[#0b57d0] text-white hover:bg-[#1a73e8] transition-all shadow-sm border-l border-[#1557b0]" style={{ borderTopRightRadius: '21px', borderBottomRightRadius: '21px', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }}>
                    <span style={{ fontSize: '0.7em' }}>▼</span>
                  </button>

                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-3" />

                  {/* Formatting Tools */}
                  <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400">
                    <span className="font-mono text-sm">Aa</span>
                  </button>
                  <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400 font-bold">
                    B
                  </button>
                  <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400 italic">
                    I
                  </button>
                  <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400 underline">
                    U
                  </button>
                  
                  <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
                  
                  <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400">
                    🔗
                  </button>
                  <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400">
                    📎
                  </button>
                </div>

                {/* Right Side Actions */}
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-200 dark:hover:bg-[#1E3A8A]/20 rounded">
                    <span className="text-gray-600 dark:text-gray-400">⋮</span>
                  </button>
                  <button className="p-2 hover:bg-gray-200 dark:hover:bg-[#1E3A8A]/20 rounded">
                    <span className="text-gray-600 dark:text-gray-400">🗑</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* LinkedIn Demo */}
          {activeTab === 'linkedin' && (
            <div className="bg-white dark:bg-[#1E3A8A]/20 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-[#1E3A8A]">
              {/* LinkedIn Header */}
              <div className="bg-white dark:bg-[#1E3A8A]/30 px-6 py-4 border-b border-gray-300 dark:border-[#1E3A8A] flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#0A66C2] text-white flex items-center justify-center text-xl font-semibold">
                  C
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">Cormac McShoul</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Head of Sales Development at TechFlow.io</div>
                </div>
              </div>

              {/* LinkedIn Message Thread */}
              <div className="p-6 min-h-[600px] bg-[#F3F2EF] dark:bg-[#0A1628] space-y-4 relative">
                {/* Received Message */}
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    C
                  </div>
                  <div className="flex-1 max-w-[80%]">
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                      <div className="text-sm text-gray-900 dark:text-white space-y-2">
                        <p>Hi there! 👋</p>
                        <p>I saw ResponseAble mentioned in a recent Sales Hacker thread and it caught my attention. Our outbound team at TestFinn is sending 200+ personalized emails per day, but drafting replies still takes too much time and they sometimes sound generic.</p>
                        <p className="font-medium">Quick questions:</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>How well does it maintain my personal writing style?</li>
                          <li>Do you have examples of results from other SaaS teams?</li>
                          <li>How long does it take to see results?</li>
                        </ul>
                        <p>Would love to see how it works if you have time for a quick demo!</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-2">10:34 AM</div>
                  </div>
                </div>

                {/* Inserted Reply - Shows after selecting a variant */}
                {!showVariants && insertedDraft && (
                  <div className="flex gap-3 justify-end">
                    <div className="flex-1 max-w-[80%] flex flex-col items-end">
                      <div className="bg-[#0A66C2] text-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm whitespace-pre-line leading-relaxed">
                          {insertedDraft}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 mr-2">Just now</div>
                    </div>
                  </div>
                )}

                {/* Draft Variants Overlay */}
                {showVariants && (
                  <motion.div
                    className="absolute left-6 right-6 top-6 bottom-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-600 shadow-2xl overflow-hidden flex flex-col z-10"
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Header */}
                    <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      <img 
                        src={demoButtonLogo}
                        alt="xRepl.ai" 
                        className="w-6 h-6 object-contain"
                      />
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                        Able to Respond Better
                      </h3>
                      <button
                        onClick={() => setShowVariants(false)}
                        className="ml-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl leading-none"
                      >
                        ✕
                      </button>
                    </div>

                    {/* Intent Section */}
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        <span className="font-semibold">Intent:</span> inquiring about AI email drafting tool features, examples, pricing, and requesting a demo call | Topics: AI email drafting, personal writing style, SaaS team examples, pricing structure, demo call
                      </p>
                      
                      {/* Strategy Buttons */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {strategies.map((strategy) => (
                          <button
                            key={strategy.label}
                            onClick={() => setSelectedStrategy(strategy.label)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                              selectedStrategy === strategy.label
                                ? 'bg-[#0A66C2] text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            {strategy.label}
                            {strategy.recommended && (
                              <span className="ml-1 text-xs bg-[#0A66C2] text-white px-1.5 py-0.5 rounded">
                                Recommended
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Goal:</span> offer a free trial or next steps
                      </p>
                    </div>

                    {/* Tone Selector */}
                    <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <label className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Tone:</span>
                        <select
                          value={selectedTone}
                          onChange={(e) => setSelectedTone(e.target.value)}
                          className="flex-1 max-w-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A66C2]"
                        >
                          {toneOptions.map((tone) => (
                            <option key={tone} value={tone}>{tone}</option>
                          ))}
                        </select>
                      </label>
                    </div>

                    {/* Draft Variants - Scrollable */}
                    <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50 dark:bg-gray-900">
                      <div className="space-y-4">
                        {variants.map((variant, index) => (
                          <motion.div
                            key={variant.label}
                            onClick={() => handleVariantClick(variant.content)}
                            className="group p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-[#0A66C2] dark:hover:border-[#0A66C2] cursor-pointer transition-all shadow-sm hover:shadow-md"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                {variant.label}
                              </span>
                              <Check className="text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" size={18} />
                            </div>
                            <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line leading-relaxed">
                              {variant.content}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* LinkedIn Message Compose Toolbar */}
              <div className="bg-white dark:bg-[#0A1628] border-t border-gray-300 dark:border-[#1E3A8A]/30">
                {/* Text Input Area */}
                <div className="px-6 pt-4 pb-2">
                  <textarea
                    placeholder={!showVariants && !insertedDraft ? "Click 'Respond' below to generate AI-powered replies..." : "Write a message..."}
                    className="w-full min-h-[100px] p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A66C2] resize-none"
                    value={insertedDraft}
                    readOnly
                  />
                </div>

                {/* Toolbar */}
                <div className="px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* Respond Button with LinkedIn styling */}
                    <button
                      onClick={() => setShowVariants(!showVariants)}
                      className="px-4 py-2 bg-[#D3E3FD] text-[#444746] hover:bg-[#C3D3ED] transition-all shadow-sm font-medium rounded-full flex items-center gap-2 group"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <img 
                          src={demoButtonLogo} 
                          alt="xReplAI" 
                          className="w-5 h-5 object-contain group-hover:scale-110 transition-transform"
                        />
                      </div>
                      xReplAI
                    </button>

                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

                    {/* LinkedIn Formatting Tools */}
                    <button className="p-2 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400">
                      <span className="font-bold">B</span>
                    </button>
                    <button className="p-2 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400">
                      <span className="italic">I</span>
                    </button>
                    <button className="p-2 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400">
                      🔗
                    </button>
                    <button className="p-2 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400">
                      😊
                    </button>
                    <button className="p-2 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20 rounded text-gray-600 dark:text-gray-400">
                      📎
                    </button>
                  </div>

                  {/* Send Button */}
                  <button className="px-5 py-2 bg-[#0A66C2] text-white hover:bg-[#004182] transition-all shadow-sm font-medium rounded-full">
                    Send
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Instruction text */}
        <motion.p
          className="text-center text-gray-600 dark:text-gray-400 mt-8 text-lg"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          Click the <span className="font-semibold text-[#7C3AED]">xReplAI</span> button to see the magic ✨
        </motion.p>
      </div>
    </section>
  );
}